import type { NexusAccount } from "@biconomy/abstractjs-canary"
import getPort, { type Options } from "get-port"
import {
  http,
  type Account,
  type Chain,
  type Hex,
  type LocalAccount,
  type PublicClient,
  type PublicRpcSchema,
  type TestClient,
  type Transport,
  type WalletClient,
  createPublicClient,
  createTestClient,
  createWalletClient,
  isHex,
  parseEther
} from "viem"
import type { BundlerClient, SmartAccount } from "viem/account-abstraction"
import { mnemonicToAccount, privateKeyToAccount } from "viem/accounts"
import type { TestClientMode } from "viem/clients/createTestClient"

export const getRandomNumber = (min = 3000, max = 9000) => {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms))

export const toClients = async ({
  rpcUrl,
  chain,
  privateKey = getFundedAccount().privateKey
}: { rpcUrl: string; chain: Chain; privateKey?: Hex }): Promise<{
  publicClient: PublicClient<Transport, Chain>
  walletClients: WalletClient<Transport, Chain, Account>[]
  testClient: TestClient<
    TestClientMode,
    Transport,
    Chain | undefined,
    Account | undefined,
    true,
    PublicRpcSchema
  >
}> => {
  const account = privateKeyToAccount(privateKey)
  const accounts = Array.from({ length: 10 }, (_, i) =>
    mnemonicToAccount(
      "test test test test test test test test test test test junk",
      { accountIndex: i }
    )
  )

  const publicClient = createPublicClient({
    chain,
    transport: http(rpcUrl)
  })

  const walletClients = accounts.map((account) =>
    createWalletClient({
      account,
      chain,
      transport: http(rpcUrl)
    })
  )

  const testClient = createTestClient({
    transport: http(rpcUrl),
    mode: "anvil"
  })

  await testClient.setBalance({
    address: account.address,
    value: parseEther("1000000000")
  })

  return { publicClient, walletClients, testClient }
}

// Declare a global variable to store excluded ports
declare global {
  var __BICONOMY_USED_PORTS__: number[]
}

// Initialize the global variable if it doesn't exist
if (!global.__BICONOMY_USED_PORTS__) {
  global.__BICONOMY_USED_PORTS__ = []
}

// Use the global variable in your PORT_OPTS
export const PORT_OPTS: Options = {
  port: 1, // overridden later
  host: "127.0.0.1",
  exclude: global.__BICONOMY_USED_PORTS__
}

export const getFreePort = async (
  max: number,
  min: number,
  options?: Options
) => {
  const potentialPort = Math.floor(Math.random() * (max - min + 1)) + min
  const mergedOptions = { ...PORT_OPTS, ...options, port: potentialPort }
  const port = await getPort(mergedOptions)
  // Add to the global list of used ports
  global.__BICONOMY_USED_PORTS__.push(port)
  return port
}

export const getFundedAccount = (): {
  account: Account
  privateKey: Hex
} => {
  if (!process.env.PRIVATE_KEY) throw new Error("PRIVATE_KEY is not set")
  const privateKey = `0x${process.env.PRIVATE_KEY}` as Hex
  return { account: privateKeyToAccount(privateKey), privateKey }
}

export type Url = `https://${string}` | `http://${string}`
export type MinimalVars = {
  privateKey: Hex
  chainId: number
  bundlerUrl: Url
}

export const getEnvVars = (prefix = ""): MinimalVars => {
  const privateKey = process.env[`${prefix}PRIVATE_KEY`]!
  if (!privateKey || !isHex(privateKey)) throw new Error("Invalid PRIVATE_KEY")
  const chainId = process.env[`${prefix}CHAIN_ID`]!
  if (!chainId || !Number.isNaN(chainId)) throw new Error("Invalid CHAIN_ID")
  const bundlerUrl = process.env[`${prefix}BUNDLER_URL`]! as Url
  if (!bundlerUrl || !bundlerUrl.includes("http"))
    throw new Error("Invalid BUNDLER_URL")

  return {
    privateKey,
    chainId: Number(chainId),
    bundlerUrl
  }
}

export type NetworkConfig = {
  account: LocalAccount
  chain: Chain
  walletClient: WalletClient<Transport, Chain, Account>
  publicClient: PublicClient<Transport, Chain>
  bundlerClient: BundlerClient<Transport, Chain, SmartAccount>
  nexusAccount: NexusAccount
}

export const validateNetwork = async ({
  chain,
  account,
  nexusAccount,
  bundlerClient,
  publicClient,
  walletClient
}: NetworkConfig) => {
  const minBalance = parseEther("0.01")
  const accountBalance = await publicClient.getBalance({
    address: account.address
  })
  if (accountBalance < minBalance * 2n) {
    throw new Error("Account from privateKey is not funded")
  }

  const nexusAccountBalance = await publicClient.getBalance({
    address: nexusAccount.address
  })
  if (nexusAccountBalance < minBalance) {
    const hash = await walletClient.sendTransaction({
      to: nexusAccount.address,
      value: minBalance
    })
    const receipt = await publicClient.getTransactionReceipt({ hash })
    if (receipt.status !== "success") {
      throw new Error("Could not top up your account")
    }
  }

  const chainIdFromBundlerClient = await bundlerClient.getChainId()
  if (chainIdFromBundlerClient !== chain.id) {
    throw new Error("Bundler Client not configured correctly")
  }
}
